import { useContext, useMemo } from "react";
import type { BikeTypeV2, LeasingOptionType } from "@jobrad-gmbh/bike-leasing-calculator";
import debounce from "debounce";
import useDeepCompareEffect from "use-deep-compare-effect";
import { AppContext, AppDispatchContext, BudgetCalculationActionTypes } from "../../state";
import { useI18n } from "../../i18n";
import { BUDGET_MONTHLY_MAX } from "../../constants/businessConstants";

const useBudgetCalculator = () => {
  const { t, formatNumber } = useI18n();
  const state = useContext(AppContext);
  const dispatch = useContext(AppDispatchContext);

  const { budgetCalculation } = state;
  const { defaultInput, userInput, serviceOptions, bikeTypeOptions, elementVisibility } = budgetCalculation;

  const mergedInput = { ...defaultInput, ...userInput };

  const handleBudgetChange = (value: number | undefined) => {
    dispatch({
      type: BudgetCalculationActionTypes.BUDGET_UPDATE_INPUT,
      payload: { monthlyBudgetBrutto: value ?? 0 },
    });
  };

  const handleServiceChange = (selectedService: LeasingOptionType | undefined) => {
    if (selectedService) {
      dispatch({
        type: BudgetCalculationActionTypes.BUDGET_UPDATE_INPUT,
        payload: {
          serviceType: selectedService.service_type,
          serviceSubType: selectedService.service_subtype,
        },
      });
    }
  };

  const handleInsurancePayerChange = (value: string) => {
    dispatch({
      type: BudgetCalculationActionTypes.BUDGET_UPDATE_INPUT,
      payload: { versicherungsZahler: value as "company" | "employee" | "self" },
    });
  };

  const handleGrantChange = (value: number | undefined) => {
    dispatch({
      type: BudgetCalculationActionTypes.BUDGET_UPDATE_INPUT,
      payload: { arbeitgeberZuschuss: value ?? 0 },
    });
  };

  const handleBikeTypeChange = (bikeType: BikeTypeV2) => {
    dispatch({
      type: BudgetCalculationActionTypes.BUDGET_UPDATE_INPUT,
      payload: { fahrradtyp: bikeType },
    });
  };

  const debouncedCalculate = useMemo(
    () =>
      debounce(() => {
        dispatch({ type: BudgetCalculationActionTypes.BUDGET_CALCULATE });
      }, 550),
    [dispatch],
  );

  useDeepCompareEffect(() => {
    debouncedCalculate();
    return () => debouncedCalculate.clear();
  }, [mergedInput, budgetCalculation.calculator?.version]);

  const serviceTypeOptions = useMemo(() => {
    return serviceOptions.map((service: LeasingOptionType) => ({
      name: t(`budgetCalculator.serviceOptions.${service.service_type}.${service.service_subtype}`),
      value: service.id,
    }));
  }, [serviceOptions, t]);

  const currentServiceValue = useMemo(() => {
    return (
      serviceOptions.find(
        (service) =>
          service.service_type === mergedInput.serviceType && service.service_subtype === mergedInput.serviceSubType,
      ) || serviceOptions[0]
    );
  }, [serviceOptions, mergedInput.serviceType, mergedInput.serviceSubType]);

  const budgetExceedsMax = (mergedInput.monthlyBudgetBrutto ?? 0) > BUDGET_MONTHLY_MAX;
  const budgetErrorMessage = budgetExceedsMax
    ? t("errorMessages.monthlyBudget.lessThanMax", { maxBudget: formatNumber(BUDGET_MONTHLY_MAX) })
    : undefined;

  const grantExceedsMax = (mergedInput.arbeitgeberZuschuss ?? 0) > BUDGET_MONTHLY_MAX;
  const grantErrorMessage = grantExceedsMax
    ? t("errorMessages.employerGrant.lessThanMax", { maxGrant: formatNumber(BUDGET_MONTHLY_MAX) })
    : undefined;

  const insurancePayerOptions = [
    { name: t("budgetCalculator.insurancePayerCompany"), value: "company" },
    { name: t("budgetCalculator.insurancePayerEmployee"), value: "employee" },
  ];

  return {
    mergedInput,
    elementVisibility,
    bikeTypeOptions,
    serviceOptions,
    serviceTypeOptions,
    currentServiceValue,
    insurancePayerOptions,
    budgetExceedsMax,
    budgetErrorMessage,
    grantExceedsMax,
    grantErrorMessage,
    handleBudgetChange,
    handleServiceChange,
    handleInsurancePayerChange,
    handleGrantChange,
    handleBikeTypeChange,
  };
};

export { useBudgetCalculator };
